import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ServiceGrid from '../components/ServiceGrid';
import ServiceModal from '../components/ServiceModal';
import apiClient from '../api';
import './pages.css';

const ServicesPage = () => {
  const { addressId } = useParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [address, setAddress] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAddressAndServices();
  }, [addressId]);

  const fetchAddressAndServices = async () => {
    try {
      setLoading(true);
      
      // Получаем информацию об адресе
      const addressResponse = await apiClient.get(`/addresses/${addressId}`);
      setAddress(addressResponse.data);

      // Получаем услуги для этого адреса
      const servicesResponse = await apiClient.get(`/services/address/${addressId}`);
      setServices(servicesResponse.data);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      alert('Не удалось загрузить данные');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingService(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (service) => {
    try {
      // Получаем полные данные услуги с тарифами
      const response = await apiClient.get(`/services/${service.id}`);
      setEditingService(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Ошибка загрузки данных услуги:', error);
      // Fallback to current service if API fails
      setEditingService(service);
      setIsModalOpen(true);
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Удалить услугу: ${service.name}?`)) {
      return;
    }

    try {
      await apiClient.delete(`/services/${service.id}`);
      setServices(prev => prev.filter(s => s.id !== service.id));
    } catch (error) {
      console.error('Ошибка удаления услуги:', error);
      alert('Не удалось удалить услугу');
    }
  };

  const handleSave = async (formData) => {
    try {
      const backendData = {
        address_id: parseInt(addressId),
        name: formData.name,
        description: formData.description || null,
        type: formData.type !== undefined ? parseInt(formData.type) : 0,
        tariffs: formData.tariffs.map(t => ({
          tariff_id: t.id || t.tariff_id
        }))
      };

      let savedService;
      if (editingService) {
        // Обновление существующей услуги
        const response = await apiClient.put(`/services/${editingService.id}`, backendData);
        savedService = response.data.service;
        setServices(prev => prev.map(s => 
          s.id === editingService.id ? savedService : s
        ));
      } else {
        // Создание новой услуги
        const response = await apiClient.post('/services', backendData);
        savedService = response.data.service;
        setServices(prev => [...prev, savedService]);
      }
      
      setIsModalOpen(false);
      setEditingService(null);
    } catch (error) {
      console.error('Ошибка сохранения услуги:', error);
      alert('Не удалось сохранить услугу');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
  };

  const handleServiceClick = (service) => {
    // Здесь можно добавить переход на страницу показаний счетчиков
    console.log('Clicked service:', service);
    navigate(`/meters/${service.id}`);
  };

  const handleBackClick = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-button" onClick={handleBackClick}>
          ← Назад к адресам
        </button>
        {address && (
          <h1 className="page-title">
            Услуги: {address.city}, {address.street} {address.house_number}
            {address.apartment_number && ` / ${address.apartment_number}`}
          </h1>
        )}
      </div>
      
      <ServiceGrid
        services={services}
        onAddClick={handleAddClick}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onServiceClick={handleServiceClick}
      />
      
      <ServiceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        service={editingService}
      />
    </div>
  );
};

export default ServicesPage;
